import React, { useContext } from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import { InternalRefetchQueryDescriptor, useMutation } from '@apollo/client';
import { Divider, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { CoherencePanelStyles, dividerStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import EntityTerminationTaskEditForm from '../actionItems/EntityTerminationTaskEditForm';
import { IEntityTerminationTask } from '../types';
import EntityTerminationTaskHistory from '../actionItems/EntityTerminationTaskHistory';
import {
  CREATE_BLOCKER_TASK,
  UPDATE_BLOCKER_TASK,
  GET_BLOCKER_QUEUE_ACTION_ITEM,
} from '../../../utils/statApi/EntityTerminationApi';
import UserContext from '../../../utils/authorization/UserContext';
import { StatUser } from '../../../utils/statApi/UsersApi';
import {
  buildOwnerInput,
  buildAssigneesInput,
} from '../../../utils/statApi/entityTerminationTaskUtils';

interface IEntityTerminationBlockerTaskEditPanelProps {
  closePanel: () => void;
  isNewTask?: boolean;
  selectedTask?: IEntityTerminationTask;
  refetchQueries?: InternalRefetchQueryDescriptor[];
  companyCode?: string;
  actionItemId: number;
  actionItemStartDate?: Date | string | null;
}

const EntityTerminationBlockerTaskEditPanel = (
  props: IEntityTerminationBlockerTaskEditPanelProps,
): JSX.Element => {
  const {
    closePanel,
    isNewTask,
    selectedTask,
    refetchQueries,
    companyCode,
    actionItemId,
    actionItemStartDate,
  } = props;
  const blockerTaskForm = useForm();
  const { handleSubmit } = blockerTaskForm;
  const styles = dividerStyles();
  const currentUser = useContext(UserContext) as StatUser | null;
  const isOwner = !!currentUser && selectedTask?.owner?.graphId === currentUser.graphGUID;
  const isAssignee =
    selectedTask?.assignees?.some((a) => a.graphId === currentUser?.graphGUID) ?? false;

  const title = isNewTask ? 'Create New ' : 'Edit ';

  const [createBlockerTask, { loading: createLoading, error: createError }] = useMutation(
    CREATE_BLOCKER_TASK,
    {
      refetchQueries: refetchQueries || [
        {
          query: GET_BLOCKER_QUEUE_ACTION_ITEM,
          variables: { actionItemId },
        },
      ],
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const [updateBlockerTask, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_BLOCKER_TASK,
    {
      refetchQueries: refetchQueries || [
        {
          query: GET_BLOCKER_QUEUE_ACTION_ITEM,
          variables: { actionItemId },
        },
      ],
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IEntityTerminationTask): void => {
    const ownerInput = buildOwnerInput(formData);
    const assigneesInput = buildAssigneesInput(formData);

    const blockerTaskVariables = {
      blockerQueueActionItemTaskDto: {
        id: selectedTask?.id ?? null,
        blockerQueueActionItemId: actionItemId,
        description: formData.description,
        owner: ownerInput,
        comment: formData.comment,
        assignees: assigneesInput,
        response: formData.response,
        followUpFlagDate: formData.followUpFlagDate,
        completedDate: formData.completedDate,
      },
      companyCode,
    };

    if (isNewTask) {
      createBlockerTask({ variables: { ...blockerTaskVariables } });
    } else {
      updateBlockerTask({ variables: { ...blockerTaskVariables } });
    }
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText={`${title} Task`}
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel={isNewTask ? 'Add Task' : 'Save Task'}
          saveTitle={isNewTask ? 'Add Task' : 'Save Task'}
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form onSubmit={handleSubmit(handleSaveClick)}>
        <EntityTerminationTaskEditForm
          form={blockerTaskForm}
          selectedTask={selectedTask}
          isOwner={isOwner}
          isAssignee={isAssignee}
          isNewTask={!!isNewTask}
          allowMultipleAssignees
          actionItemStartDate={actionItemStartDate}
        />
        <LoadingErrorMessage
          loading={createLoading}
          error={createError}
          actionName="creating the task"
        />
        <LoadingErrorMessage
          loading={updateLoading}
          error={updateError}
          actionName="updating the task"
        />
      </form>
      <FluentProvider theme={webLightTheme}>
        <Divider className={styles.divider} />
      </FluentProvider>

      {!isNewTask && (
        <>
          <EntityTerminationTaskHistory selectedtask={selectedTask} />
        </>
      )}
    </CoherencePanel>
  );
};

export default EntityTerminationBlockerTaskEditPanel;
